<template>
  <div class="stepwise-view">
    <!-- Toast 提示 -->
    <div v-if="toast.show" class="toast-container" :class="toast.type" role="status" aria-live="polite">
      <span class="toast-icon" aria-hidden="true">{{ toast.icon }}</span>
      <span class="toast-message">{{ toast.message }}</span>
    </div>

    <div class="stepwise-page">
      <header class="page-header">
        <h1 class="page-title">精细视频翻译</h1>
        <p class="page-subtitle">分两步完成视频翻译：先自动生成字幕，人工校准后再做语音克隆与对口型</p>
        <p class="notice-text">
          注意：对口型仅能处理全程单人出镜且每一帧都有正脸的视频，无法解决多人、转头、面部遮挡或无脸画面。字幕识别可能存在错漏，分步翻译可以让您先修正字幕，再合成语音。
        </p>
      </header>

      <!-- 流程指示 -->
      <ol class="stepper" aria-label="处理流程">
        <li class="stepper-item" :class="{ 'is-active': currentStep === 1, 'is-done': step1Status === 'completed' }">
          <span class="stepper-index" aria-hidden="true">{{ step1Status === 'completed' ? '✓' : '1' }}</span>
          <span class="stepper-text">
            <strong>第一步 生成字幕</strong>
            <small>上传视频并选择目标语言，生成 SRT 字幕</small>
          </span>
        </li>
        <li class="stepper-arrow" aria-hidden="true">→</li>
        <li class="stepper-item" :class="{ 'is-active': currentStep === 2, 'is-done': step2Status === 'completed' }">
          <span class="stepper-index" aria-hidden="true">{{ step2Status === 'completed' ? '✓' : '2' }}</span>
          <span class="stepper-text">
            <strong>第二步 校准字幕后合成</strong>
            <small>下载字幕修改后重新上传，完成克隆翻译</small>
          </span>
        </li>
      </ol>

      <!-- ==================== 第一步：生成字幕 ==================== -->
      <section class="step-card" aria-labelledby="step1-title">
        <header class="step-card__header">
          <h2 id="step1-title" class="step-card__title">第一步：生成视频字幕（SRT）</h2>
          <span class="status-chip" :class="'status-chip--' + step1Status">{{ step1StatusText }}</span>
        </header>

        <div class="step-card__body">
          <div class="step-card__media">
            <input
              id="stepwise-video-input"
              ref="fileInput"
              class="sr-only"
              type="file"
              accept="video/*"
              @change="handleFileSelect"
            >
            <label
              v-if="!selectedFile"
              for="stepwise-video-input"
              class="upload-box"
              :class="{ dragging: isDragging }"
              @drop.prevent="handleFileDrop"
              @dragover.prevent="isDragging = true"
              @dragleave.prevent="isDragging = false"
            >
              <svg class="upload-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M12 5v14M5 12h14" />
              </svg>
              <span class="upload-title">点击上传或拖拽本人素材</span>
              <small class="upload-hint">支持 MP4 等常见格式，时长不超过 60 分钟</small>
            </label>

            <div v-else class="video-player-wrapper">
              <button type="button" class="delete-video-btn" aria-label="删除已上传的视频" @click="confirmDelete">×</button>
              <video ref="videoPlayer" controls playsinline @loadedmetadata="getVideoMetadata">
                <source :src="videoUrl" type="video/mp4">
                您的浏览器不支持 video 标签。
              </video>
              <p class="file-meta">
                {{ selectedFile.name }}
                <span v-if="videoDuration > 0"> · {{ videoDurationMinutes.toFixed(2) }} 分钟</span>
              </p>
            </div>
          </div>

          <div class="step-card__settings">
            <div class="field">
              <label class="field__label" for="stepwise-target-lang">
                翻译的目标语言 <span class="required" aria-hidden="true">*</span>
              </label>
              <select id="stepwise-target-lang" v-model="targetLanguage" class="input">
                <option v-for="item in languages" :key="item.value" :value="item.value">{{ item.label }}</option>
              </select>
              <p class="field__hint">生成的字幕会翻译成该语言，第二步的语音克隆也按该语言合成。</p>
            </div>

            <div class="field">
              <label class="field__label" for="stepwise-subtitle-type">返回的字幕类型</label>
              <select id="stepwise-subtitle-type" v-model="subtitleLanguageType" class="input">
                <option value="target">翻译后字幕（推荐）</option>
                <option value="bilingual">双语字幕（原文 + 译文）</option>
                <option value="original">原始语言字幕</option>
              </select>
              <p class="field__hint">推荐「翻译后字幕」：直接修改译文后再合成，翻译准确率最高。</p>
            </div>

            <div class="cost-row">
              <span>预计费用</span>
              <strong>{{ step1Cost.toFixed(2) }} 元</strong>
              <small v-if="videoDuration > 0">（{{ videoDurationMinutes.toFixed(2) }} 分钟 × {{ pricing.step1 }} 元/分钟）</small>
            </div>

            <button
              type="button"
              class="primary-btn"
              :disabled="!canSubmitStep1"
              :aria-busy="isSubmittingStep1 ? 'true' : 'false'"
              @click="startStep1"
            >
              {{ isSubmittingStep1 ? '提交中…' : '生成字幕' }}
            </button>

            <div class="status-panel" role="status" aria-live="polite">
              <p v-if="step1Status === 'idle'">上传视频并点击「生成字幕」，处理完成后可下载 SRT 文件。</p>
              <p v-else-if="step1Status === 'pending' || step1Status === 'processing'">
                字幕生成中，请稍候……页面会自动刷新状态。
              </p>
              <p v-else-if="step1Status === 'completed'" class="status-panel__done">
                字幕已生成，请下载到本地修改，然后在第二步上传。
              </p>
              <p v-else-if="step1Status === 'failed'" class="status-panel__failed">
                字幕生成失败，费用已退回，请重试。
              </p>

              <div class="status-actions">
                <button
                  v-if="step1Status === 'completed' && step1SrtUrl"
                  type="button"
                  class="secondary-btn"
                  @click="triggerDownload(step1SrtUrl, srtDownloadName)"
                >
                  下载 SRT 字幕
                </button>
                <button
                  v-if="step1TaskId && (step1Status === 'pending' || step1Status === 'processing')"
                  type="button"
                  class="secondary-btn"
                  @click="refreshStep1Status"
                >
                  刷新状态
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ==================== 第二步：上传字幕并合成 ==================== -->
      <section class="step-card" :class="{ 'is-locked': !step1Completed }" aria-labelledby="step2-title">
        <header class="step-card__header">
          <h2 id="step2-title" class="step-card__title">第二步：上传校准字幕后合成</h2>
          <span class="status-chip" :class="'status-chip--' + step2Status">{{ step2StatusText }}</span>
        </header>

        <p v-if="!step1Completed" class="locked-hint">
          请先完成第一步并下载字幕；修改完成后回到本页上传，即可开始语音克隆翻译。
        </p>

        <div class="step-card__body">
          <div class="step-card__media">
            <input
              id="stepwise-srt-input"
              ref="srtInput"
              class="sr-only"
              type="file"
              accept=".srt"
              :disabled="!step1Completed"
              @change="handleSrtSelect"
            >
            <label
              for="stepwise-srt-input"
              class="upload-box upload-box--srt"
              :class="{ dragging: isSrtDragging, disabled: !step1Completed }"
              @drop.prevent="handleSrtDrop"
              @dragover.prevent="handleSrtDragOver"
              @dragleave.prevent="isSrtDragging = false"
            >
              <svg class="upload-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M12 5v14M5 12h14" />
              </svg>
              <span class="upload-title">{{ srtFileName || '点击上传或拖拽修改后的 .srt 字幕' }}</span>
              <small class="upload-hint">仅支持 .srt 文件，大小不超过 10MB</small>
            </label>

            <div v-if="srtFileName" class="srt-file-row">
              <span class="srt-file-name" :title="srtFileName">{{ srtFileName }}</span>
              <button type="button" class="text-btn" @click="clearSrtFile">移除</button>
            </div>
          </div>

          <div class="step-card__settings">
            <div class="toggle-field">
              <div class="toggle-field__text">
                <span class="field__label">是否对口型</span>
                <p class="field__hint">生成视频时同步调整口型以匹配翻译后的语音（{{ pricing.lipSync }} 元/分钟）</p>
              </div>
              <label class="toggle-switch">
                <input v-model="lipSync" type="checkbox" :disabled="!step1Completed" aria-label="是否对口型">
                <span class="toggle-slider" aria-hidden="true"></span>
              </label>
            </div>

            <div class="toggle-field">
              <div class="toggle-field__text">
                <span class="field__label">是否写入硬字幕</span>
                <p class="field__hint">将校准后的字幕烧录到画面中，生成后不可关闭</p>
              </div>
              <label class="toggle-switch">
                <input v-model="hardSubtitle" type="checkbox" :disabled="!step1Completed" aria-label="是否写入硬字幕">
                <span class="toggle-slider" aria-hidden="true"></span>
              </label>
            </div>

            <div class="cost-row">
              <span>预计费用</span>
              <strong>{{ step2Cost.toFixed(2) }} 元</strong>
              <small v-if="videoDuration > 0">（{{ videoDurationMinutes.toFixed(2) }} 分钟 × {{ step2Rate }} 元/分钟）</small>
            </div>

            <button
              type="button"
              class="primary-btn"
              :disabled="!canSubmitStep2"
              :aria-busy="isSubmittingStep2 ? 'true' : 'false'"
              @click="startStep2"
            >
              {{ isSubmittingStep2 ? '提交中…' : '开始克隆翻译' }}
            </button>

            <div class="status-panel" role="status" aria-live="polite">
              <p v-if="step2Status === 'idle'">上传修改后的字幕并点击「开始克隆翻译」。</p>
              <p v-else-if="step2Status === 'pending' || step2Status === 'processing'">
                语音克隆翻译处理中，请稍候……页面会自动刷新状态。
              </p>
              <p v-else-if="step2Status === 'completed'" class="status-panel__done">
                翻译完成，可以下载最终视频了。
              </p>
              <p v-else-if="step2Status === 'failed'" class="status-panel__failed">
                处理失败，费用已退回，请检查字幕文件后重试。
              </p>

              <div class="status-actions">
                <button
                  v-if="step2Status === 'completed' && step2VideoUrl"
                  type="button"
                  class="secondary-btn"
                  @click="triggerDownload(step2VideoUrl, videoDownloadName)"
                >
                  下载翻译视频
                </button>
                <button
                  v-if="step2TaskId && (step2Status === 'pending' || step2Status === 'processing')"
                  type="button"
                  class="secondary-btn"
                  @click="refreshStep2Status"
                >
                  刷新状态
                </button>
                <button
                  v-if="step2Status === 'completed'"
                  type="button"
                  class="secondary-btn"
                  @click="resetAll"
                >
                  再翻译一个视频
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <p class="terms">
        仅对本人视频翻译，您承诺您的素材内容为您本人所有或已获得合法授权。并遵守《音视频翻译功能使用规范》
      </p>
    </div>

    <!-- 删除确认 -->
    <div v-if="showDeleteConfirm" class="modal-overlay" @click.self="showDeleteConfirm = false">
      <div class="modal" role="dialog" aria-modal="true" aria-labelledby="delete-title">
        <h3 id="delete-title">确认删除</h3>
        <p>删除后已生成的字幕与任务状态都会重置，确定要删除吗？</p>
        <div class="modal-buttons">
          <button type="button" class="secondary-btn" @click="showDeleteConfirm = false">取消</button>
          <button type="button" class="danger-btn" @click="handleDeleteConfirm">删除</button>
        </div>
      </div>
    </div>

    <!-- 费用确认 -->
    <div v-if="showConfirmPricing" class="modal-overlay" @click.self="showConfirmPricing = false">
      <div class="modal" role="dialog" aria-modal="true" aria-labelledby="pricing-title">
        <h3 id="pricing-title">确认费用</h3>
        <p>{{ confirmMessage }}</p>
        <div class="modal-buttons">
          <button type="button" class="secondary-btn" @click="showConfirmPricing = false">取消</button>
          <button type="button" class="primary-btn" @click="handleConfirmPricing">确定</button>
        </div>
      </div>
    </div>

    <!-- 提交中 -->
    <div v-if="isProcessing" class="modal-overlay">
      <div class="modal modal--center" role="alertdialog" aria-modal="true" aria-live="assertive">
        <span class="spinner" aria-hidden="true"></span>
        <h3>{{ processingMessage }}</h3>
        <p>文件上传中，请勿关闭页面……</p>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getUpQiniuToken,
  uploadToQiniu,
  getUserRecords,
  saveStepwiseVideoSrtFileUrl,
  saveStepwiseVideoTranslationFileUrl
} from '@/api/upfile'
import { QINIU_BASE_URL, stepwiseVideoTranslateLanguages } from '@/api/constants'
import { mapGetters } from 'vuex'

const MAX_VIDEO_DURATION = 60 * 60 // 视频最长 60 分钟（秒）
const MAX_VIDEO_SIZE = 2000 * 1024 * 1024 // 视频最大 2000MB
const MAX_SRT_SIZE = 10 * 1024 * 1024 // 字幕最大 10MB
const POLL_INTERVAL = 8000 // 轮询间隔 8 秒
const MAX_POLL_TIMES = 150 // 最多轮询 150 次（约 20 分钟）

export default {
  name: 'StepwiseVideoTranslation',
  data () {
    return {
      // 语言与字幕选项
      targetLanguage: 'en',
      subtitleLanguageType: 'target',
      languages: stepwiseVideoTranslateLanguages,

      // 第一步：视频与字幕生成
      selectedFile: null,
      videoUrl: '',
      videoDuration: 0,
      isDragging: false,
      uploadedVideoUrl: '',
      step1TaskId: '',
      step1Status: 'idle',
      step1SrtUrl: '',
      isSubmittingStep1: false,

      // 第二步：校准字幕后合成
      srtFile: null,
      srtFileName: '',
      isSrtDragging: false,
      lipSync: false,
      hardSubtitle: false,
      step2TaskId: '',
      step2Status: 'idle',
      step2VideoUrl: '',
      isSubmittingStep2: false,

      // 弹窗与提示
      showDeleteConfirm: false,
      showConfirmPricing: false,
      pendingAction: '',
      confirmMessage: '',
      isProcessing: false,
      processingMessage: '',
      toast: {
        show: false,
        message: '',
        type: 'success',
        icon: '✓'
      },
      // 轮询与提示计时器（仅内部使用，不参与渲染）
      step1Timer: null,
      step2Timer: null,
      step1PollTimes: 0,
      step2PollTimes: 0,
      toastTimer: null,

      // 价格配置（元/分钟）
      pricing: {
        step1: 1,
        step2: 3,
        lipSync: 4
      }
    }
  },
  computed: {
    ...mapGetters(['balance', 'isLoggedIn']),
    videoDurationMinutes () {
      return this.videoDuration / 60
    },
    step1Cost () {
      return this.videoDurationMinutes * this.pricing.step1
    },
    step2Rate () {
      return this.lipSync ? this.pricing.lipSync : this.pricing.step2
    },
    step2Cost () {
      return this.videoDurationMinutes * this.step2Rate
    },
    currentStep () {
      return this.step1Status === 'completed' ? 2 : 1
    },
    step1Completed () {
      return this.step1Status === 'completed'
    },
    canSubmitStep1 () {
      if (this.isSubmittingStep1) return false
      if (this.step1Status === 'pending' || this.step1Status === 'processing') return false
      return !!this.selectedFile && this.videoDuration > 0
    },
    canSubmitStep2 () {
      if (!this.step1Completed || this.isSubmittingStep2) return false
      if (this.step2Status === 'pending' || this.step2Status === 'processing') return false
      return !!this.uploadedVideoUrl && !!this.srtFile && this.videoDuration > 0
    },
    step1StatusText () {
      return this.statusText(this.step1Status)
    },
    step2StatusText () {
      return this.statusText(this.step2Status)
    },
    baseFileName () {
      const name = (this.selectedFile && this.selectedFile.name) || 'subtitle'
      return name.replace(/\.[^/.]+$/, '')
    },
    srtDownloadName () {
      return `${this.baseFileName}_${this.targetLanguage}.srt`
    },
    videoDownloadName () {
      return `${this.baseFileName}_translated.mp4`
    }
  },
  beforeDestroy () {
    this.stopPolling('step1')
    this.stopPolling('step2')
    if (this.videoUrl) {
      URL.revokeObjectURL(this.videoUrl)
      this.videoUrl = ''
    }
    if (this.toastTimer) {
      clearTimeout(this.toastTimer)
    }
  },
  methods: {
    // ─── 通用提示 ──────────────────────────────────────────
    showToast (message, type = 'success') {
      const icons = { success: '✓', error: '✕', warning: '⚠', info: 'ℹ' }
      this.toast.message = message
      this.toast.type = type
      this.toast.icon = icons[type] || icons.info
      this.toast.show = true
      if (this.toastTimer) clearTimeout(this.toastTimer)
      this.toastTimer = setTimeout(() => { this.toast.show = false }, 4000)
    },
    showError (message) { this.showToast(message, 'error') },
    showSuccess (message) { this.showToast(message, 'success') },
    statusText (status) {
      const map = {
        idle: '未开始',
        pending: '排队中',
        processing: '处理中',
        completed: '已完成',
        failed: '处理失败'
      }
      return map[status] || '未开始'
    },
    triggerDownload (url, filename) {
      if (!url) return
      const link = document.createElement('a')
      link.href = url
      link.download = filename || ''
      link.rel = 'noopener'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      this.showToast('已开始下载，若浏览器未响应请在新页面中右键另存为', 'info')
    },
    generateQiniuFileName (prefix, originalFilename) {
      const now = new Date()
      const pad = n => String(n).padStart(2, '0')
      const datePart = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`
      const timePart = `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`
      const ms = String(now.getMilliseconds()).padStart(3, '0') + Math.floor(Math.random() * 1000).toString().padStart(3, '0')
      let uuidShort
      if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        uuidShort = crypto.randomUUID().replace(/-/g, '').slice(0, 4)
      } else {
        uuidShort = Math.random().toString(16).substring(2, 6)
      }
      const ext = (originalFilename || '').split('.').pop() || 'bin'
      return `${prefix}_${datePart}_${timePart}_${ms}_${uuidShort}.${ext}`
    },

    // ─── 视频上传 ──────────────────────────────────────────
    handleFileSelect (event) {
      const file = event.target.files[0]
      if (file && file.type.startsWith('video/')) {
        this.processVideoFile(file)
      } else if (file) {
        this.showError('请选择有效的视频文件')
      }
    },
    handleFileDrop (event) {
      this.isDragging = false
      const file = event.dataTransfer.files[0]
      if (file && file.type.startsWith('video/')) {
        this.processVideoFile(file)
      } else if (file) {
        this.showError('请拖拽有效的视频文件')
      }
    },
    processVideoFile (file) {
      if (file.size > MAX_VIDEO_SIZE) {
        this.showError('视频文件过大，最大支持 2000MB')
        return
      }
      // 更换视频时，重置两步的处理状态
      this.stopPolling('step1')
      this.stopPolling('step2')
      if (this.videoUrl) URL.revokeObjectURL(this.videoUrl)
      this.selectedFile = file
      this.videoUrl = URL.createObjectURL(file)
      this.videoDuration = 0
      this.uploadedVideoUrl = ''
      this.step1TaskId = ''
      this.step1Status = 'idle'
      this.step1SrtUrl = ''
      this.clearSrtFile()
      this.step2TaskId = ''
      this.step2Status = 'idle'
      this.step2VideoUrl = ''
    },
    getVideoMetadata () {
      const video = this.$refs.videoPlayer
      if (!video) return
      this.videoDuration = video.duration || 0
      if (video.duration > MAX_VIDEO_DURATION) {
        this.showError('视频时长超过限制（最大 60 分钟）')
        this.resetAll()
      }
    },
    confirmDelete () {
      this.showDeleteConfirm = true
    },
    handleDeleteConfirm () {
      this.showDeleteConfirm = false
      this.resetAll()
      this.showToast('已删除视频与任务状态', 'info')
    },
    resetAll () {
      this.stopPolling('step1')
      this.stopPolling('step2')
      if (this.videoUrl) URL.revokeObjectURL(this.videoUrl)
      this.videoUrl = ''
      this.selectedFile = null
      this.videoDuration = 0
      this.uploadedVideoUrl = ''
      this.step1TaskId = ''
      this.step1Status = 'idle'
      this.step1SrtUrl = ''
      this.clearSrtFile()
      this.step2TaskId = ''
      this.step2Status = 'idle'
      this.step2VideoUrl = ''
      this.lipSync = false
      this.hardSubtitle = false
      if (this.$refs.fileInput) this.$refs.fileInput.value = ''
    },

    // ─── 字幕文件 ──────────────────────────────────────────
    handleSrtDragOver () {
      if (!this.step1Completed) return
      this.isSrtDragging = true
    },
    handleSrtSelect (event) {
      const file = event.target.files[0]
      if (file) this.processSrtFile(file)
    },
    handleSrtDrop (event) {
      this.isSrtDragging = false
      if (!this.step1Completed) {
        this.showError('请先完成第一步生成字幕')
        return
      }
      const file = event.dataTransfer.files[0]
      if (file) this.processSrtFile(file)
    },
    processSrtFile (file) {
      const isSrt = /\.srt$/i.test(file.name)
      if (!isSrt) {
        this.showError('请上传 .srt 格式的字幕文件')
        return
      }
      if (file.size > MAX_SRT_SIZE) {
        this.showError('字幕文件过大，最大支持 10MB')
        return
      }
      this.srtFile = file
      this.srtFileName = file.name
      this.step2Status = 'idle'
      this.step2TaskId = ''
      this.step2VideoUrl = ''
    },
    clearSrtFile () {
      this.srtFile = null
      this.srtFileName = ''
      if (this.$refs.srtInput) this.$refs.srtInput.value = ''
    },

    // ─── 第一步：生成字幕 ──────────────────────────────────
    startStep1 () {
      if (!this.isLoggedIn) { this.showError('请先登录'); return }
      if (!this.selectedFile) { this.showError('请先选择一个视频文件'); return }
      if (!this.videoDuration || this.videoDuration <= 0) { this.showError('视频信息读取中，请稍后再试'); return }
      if (this.videoDuration > MAX_VIDEO_DURATION) { this.showError('视频时长不能超过 60 分钟'); return }
      if (this.balance < this.step1Cost) {
        this.showError(`余额不足，当前余额 ¥${this.balance}，所需 ¥${this.step1Cost.toFixed(2)}`)
        return
      }
      this.pendingAction = 'step1'
      this.confirmMessage = `视频时长 ${this.videoDurationMinutes.toFixed(2)} 分钟，按 ${this.pricing.step1} 元/分钟计费，` +
        `本次生成字幕费用 ${this.step1Cost.toFixed(2)} 元。确认开始吗？`
      this.showConfirmPricing = true
    },
    handleConfirmPricing () {
      this.showConfirmPricing = false
      if (this.pendingAction === 'step1') {
        this.submitStep1()
      } else if (this.pendingAction === 'step2') {
        this.submitStep2()
      }
      this.pendingAction = ''
    },
    async submitStep1 () {
      this.isSubmittingStep1 = true
      this.isProcessing = true
      this.processingMessage = '正在上传视频……'
      const file = this.selectedFile
      try {
        const tokenResponse = await getUpQiniuToken()
        const qiniuUploadToken = tokenResponse.uploadToken
        const uploadFileName = this.generateQiniuFileName('stepwiseVideoSrt', file.name)
        const response = await uploadToQiniu(file, uploadFileName, qiniuUploadToken)
        this.uploadedVideoUrl = `${QINIU_BASE_URL}/${response.data.key}`

        const requestData = {
          operation_type: 'stepwiseVideoSrt',
          input_file_url: this.uploadedVideoUrl,
          input_filename: file.name,
          target_lang: this.targetLanguage,
          subtitle_language_type: this.subtitleLanguageType,
          video_duration: this.videoDuration
        }
        const result = await saveStepwiseVideoSrtFileUrl(requestData)
        if (result && result.status === 200) {
          if (typeof result.balance !== 'undefined') {
            this.$store.commit('setBalance', result.balance)
          }
          this.step1TaskId = result.task_id || ''
          this.step1Status = 'pending'
          this.step1SrtUrl = ''
          this.showSuccess('视频已上传，字幕生成任务已提交')
          this.startPolling('step1')
        } else {
          this.showError((result && result.message) || '服务端返回异常，请稍后重试')
        }
      } catch (error) {
        console.error('生成字幕失败:', error)
        this.showError('网络错误或服务异常，请稍后重试')
      } finally {
        this.isSubmittingStep1 = false
        this.isProcessing = false
      }
    },

    // ─── 第二步：克隆翻译合成 ──────────────────────────────
    startStep2 () {
      if (!this.isLoggedIn) { this.showError('请先登录'); return }
      if (!this.step1Completed) { this.showError('请先完成第一步生成字幕'); return }
      if (!this.srtFile) { this.showError('请上传修改后的 .srt 字幕文件'); return }
      if (this.balance < this.step2Cost) {
        this.showError(`余额不足，当前余额 ¥${this.balance}，所需 ¥${this.step2Cost.toFixed(2)}`)
        return
      }
      this.pendingAction = 'step2'
      this.confirmMessage = `视频时长 ${this.videoDurationMinutes.toFixed(2)} 分钟，按 ${this.step2Rate} 元/分钟计费，` +
        `本次克隆翻译费用 ${this.step2Cost.toFixed(2)} 元。确认开始吗？`
      this.showConfirmPricing = true
    },
    async submitStep2 () {
      this.isSubmittingStep2 = true
      this.isProcessing = true
      this.processingMessage = '正在上传字幕……'
      try {
        const tokenResponse = await getUpQiniuToken()
        const qiniuUploadToken = tokenResponse.uploadToken
        const srtFileName = this.generateQiniuFileName('stepwiseVideoSrtEdited', this.srtFile.name)
        const response = await uploadToQiniu(this.srtFile, srtFileName, qiniuUploadToken)
        const subtitleFileUrl = `${QINIU_BASE_URL}/${response.data.key}`

        const requestData = {
          operation_type: 'stepwiseVideoTranslation',
          input_file_url: this.uploadedVideoUrl,
          input_filename: this.selectedFile ? this.selectedFile.name : '',
          subtitle_file_url: subtitleFileUrl,
          target_lang: this.targetLanguage,
          lipSync: this.lipSync,
          hardSubtitle: this.hardSubtitle,
          video_duration: this.videoDuration
        }
        const result = await saveStepwiseVideoTranslationFileUrl(requestData)
        if (result && result.status === 200) {
          if (typeof result.balance !== 'undefined') {
            this.$store.commit('setBalance', result.balance)
          }
          this.step2TaskId = result.task_id || ''
          this.step2Status = 'pending'
          this.step2VideoUrl = ''
          this.showSuccess('字幕已上传，克隆翻译任务已提交')
          this.startPolling('step2')
        } else {
          this.showError((result && result.message) || '服务端返回异常，请稍后重试')
        }
      } catch (error) {
        console.error('提交克隆翻译失败:', error)
        this.showError('网络错误或服务异常，请稍后重试')
      } finally {
        this.isSubmittingStep2 = false
        this.isProcessing = false
      }
    },

    // ─── 状态轮询 ──────────────────────────────────────────
    startPolling (step) {
      this.stopPolling(step)
      const key = step === 'step1' ? 'step1PollTimes' : 'step2PollTimes'
      this[key] = 0
      const timerKey = step === 'step1' ? 'step1Timer' : 'step2Timer'
      this[timerKey] = setInterval(() => {
        if (step === 'step1') {
          this.refreshStep1Status()
        } else {
          this.refreshStep2Status()
        }
        this[key] += 1
        if (this[key] >= MAX_POLL_TIMES) {
          this.stopPolling(step)
        }
      }, POLL_INTERVAL)
    },
    stopPolling (step) {
      const timerKey = step === 'step1' ? 'step1Timer' : 'step2Timer'
      if (this[timerKey]) {
        clearInterval(this[timerKey])
        this[timerKey] = null
      }
    },
    async fetchRecord (tableName, taskId) {
      if (!taskId) return null
      const response = await getUserRecords({ table_names: [tableName] })
      const rows = (response && response.result) || []
      return rows.find(row => row.task_id === taskId) || null
    },
    async refreshStep1Status () {
      if (!this.step1TaskId) return
      try {
        const record = await this.fetchRecord('stepwiseVideoSrt', this.step1TaskId)
        if (!record) return
        this.step1Status = record.task_status || this.step1Status
        if (record.result_file_url) this.step1SrtUrl = record.result_file_url
        if (this.step1Status === 'completed') {
          this.stopPolling('step1')
          this.showSuccess('字幕已生成，请下载后修改')
        } else if (this.step1Status === 'failed') {
          this.stopPolling('step1')
          this.showError('字幕生成失败，费用已退回')
        }
      } catch (error) {
        console.error('查询字幕状态失败:', error)
      }
    },
    async refreshStep2Status () {
      if (!this.step2TaskId) return
      try {
        const record = await this.fetchRecord('stepwiseVideoTranslation', this.step2TaskId)
        if (!record) return
        this.step2Status = record.task_status || this.step2Status
        if (record.result_file_url) this.step2VideoUrl = record.result_file_url
        if (this.step2Status === 'completed') {
          this.stopPolling('step2')
          this.showSuccess('视频翻译完成，可以下载了')
        } else if (this.step2Status === 'failed') {
          this.stopPolling('step2')
          this.showError('视频翻译失败，费用已退回')
        }
      } catch (error) {
        console.error('查询翻译状态失败:', error)
      }
    }
  }
}
</script>

<style scoped>
/* ========== 页面基础 ========== */
.stepwise-view {
  min-height: 100%;
  background-color: #141414;
  color: #f5f5f5;
}

.stepwise-page {
  max-width: 1080px;
  margin: 0 auto;
  padding: 24px 20px 48px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
}

.page-header {
  text-align: center;
}

.page-title {
  margin: 8px 0 6px;
  font-size: 24px;
  font-weight: 600;
}

.page-subtitle {
  margin: 0 0 10px;
  color: #b9b9b9;
  font-size: 15px;
  line-height: 1.5;
}

.notice-text {
  margin: 0 auto 20px;
  max-width: 820px;
  color: #ffb74d;
  font-size: 13px;
  line-height: 1.6;
}

/* ========== 流程条 ========== */
.stepper {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 12px;
  margin: 0 0 20px;
  padding: 16px;
  list-style: none;
  background-color: #1f1f1f;
  border: 1px solid #2f2f2f;
  border-radius: 14px;
}

.stepper-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 10px;
  border-radius: 10px;
}

.stepper-item.is-active {
  background-color: rgba(66, 133, 244, 0.14);
}

.stepper-item.is-done .stepper-index {
  background-color: #2e7d32;
  border-color: #2e7d32;
}

.stepper-index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex: 0 0 32px;
  border: 1px solid #5a5a5a;
  border-radius: 50%;
  font-weight: 600;
  font-size: 15px;
}

.stepper-text {
  display: flex;
  flex-direction: column;
  line-height: 1.35;
}

.stepper-text strong {
  font-size: 14px;
  font-weight: 600;
}

.stepper-text small {
  color: #9e9e9e;
  font-size: 12px;
}

.stepper-arrow {
  color: #6b6b6b;
  font-size: 16px;
}

/* ========== 步骤卡片 ========== */
.step-card {
  margin-bottom: 20px;
  padding: 20px;
  background-color: #1f1f1f;
  border: 1px solid #2f2f2f;
  border-radius: 16px;
}

.step-card.is-locked {
  opacity: 0.72;
}

.step-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.step-card__title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.status-chip {
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid #4a4a4a;
  color: #dcdcdc;
  background-color: rgba(255, 255, 255, 0.06);
}

.status-chip--pending,
.status-chip--processing {
  color: #ffe082;
  border-color: rgba(255, 224, 130, 0.45);
  background-color: rgba(255, 224, 130, 0.12);
}

.status-chip--completed {
  color: #a5d6a7;
  border-color: rgba(165, 214, 167, 0.45);
  background-color: rgba(46, 125, 50, 0.22);
}

.status-chip--failed {
  color: #ef9a9a;
  border-color: rgba(239, 154, 154, 0.45);
  background-color: rgba(198, 40, 40, 0.2);
}

.locked-hint {
  margin: 0 0 14px;
  padding: 10px 14px;
  border-radius: 10px;
  background-color: rgba(255, 183, 77, 0.12);
  color: #ffcc80;
  font-size: 13px;
  line-height: 1.5;
}

.step-card__body {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 20px;
  align-items: start;
}

/* ========== 上传区 ========== */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.upload-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 210px;
  padding: 24px 18px;
  text-align: center;
  background-color: #262626;
  border: 1.5px dashed #4d4d4d;
  border-radius: 14px;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.upload-box:hover,
.upload-box.dragging {
  border-color: #4285f4;
  background-color: rgba(66, 133, 244, 0.08);
}

.upload-box.disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

#stepwise-video-input:focus-visible + .upload-box,
#stepwise-srt-input:focus-visible + .upload-box {
  outline: 2px solid #4285f4;
  outline-offset: 3px;
}

.upload-icon {
  width: 34px;
  height: 34px;
  color: #8ab4f8;
}

.upload-title {
  font-size: 15px;
  font-weight: 500;
  word-break: break-all;
}

.upload-hint {
  color: #9e9e9e;
  font-size: 12px;
  line-height: 1.5;
}

.video-player-wrapper {
  position: relative;
  border-radius: 14px;
  overflow: hidden;
  background-color: #000;
}

.video-player-wrapper video {
  display: block;
  width: 100%;
  max-height: 320px;
  background-color: #000;
}

.delete-video-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.6);
  color: #fff;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  z-index: 2;
}

.delete-video-btn:hover {
  background-color: rgba(198, 40, 40, 0.85);
}

.delete-video-btn:focus-visible,
.primary-btn:focus-visible,
.secondary-btn:focus-visible,
.danger-btn:focus-visible,
.text-btn:focus-visible,
.toggle-switch input:focus-visible + .toggle-slider {
  outline: 2px solid #4285f4;
  outline-offset: 2px;
}

.file-meta {
  margin: 0;
  padding: 10px 12px;
  color: #cfcfcf;
  font-size: 13px;
  word-break: break-all;
  background-color: #1a1a1a;
}

.srt-file-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 12px;
  padding: 10px 14px;
  background-color: #262626;
  border-radius: 10px;
}

.srt-file-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
}

/* ========== 表单 ========== */
.field {
  margin-bottom: 16px;
}

.field__label {
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 600;
  color: #ececec;
}

.field__hint {
  margin: 6px 0 0;
  color: #9e9e9e;
  font-size: 12px;
  line-height: 1.5;
}

.required {
  color: #ef9a9a;
}

.input {
  width: 100%;
  min-height: 44px;
  padding: 10px 12px;
  color: #f5f5f5;
  background-color: #262626;
  border: 1px solid #454545;
  border-radius: 10px;
  font-size: 14px;
  cursor: pointer;
}

.input:focus-visible {
  outline: 2px solid #4285f4;
  outline-offset: 1px;
}

.input option {
  color: #111;
}

.toggle-field {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
  padding: 12px 14px;
  background-color: #262626;
  border-radius: 10px;
}

.toggle-field__text {
  min-width: 0;
}

.toggle-field__text .field__label {
  margin-bottom: 4px;
}

.toggle-switch {
  position: relative;
  display: inline-flex;
  align-items: center;
  width: 52px;
  height: 44px;
  flex: 0 0 52px;
  cursor: pointer;
}

.toggle-switch input {
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  cursor: pointer;
}

.toggle-slider {
  position: relative;
  width: 48px;
  height: 26px;
  border-radius: 999px;
  background-color: #505050;
  transition: background-color 0.2s ease;
}

.toggle-slider::after {
  content: '';
  position: absolute;
  top: 3px;
  left: 3px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #fff;
  transition: transform 0.2s ease;
}

.toggle-switch input:checked + .toggle-slider {
  background-color: #4285f4;
}

.toggle-switch input:checked + .toggle-slider::after {
  transform: translateX(22px);
}

.toggle-switch input:disabled + .toggle-slider {
  opacity: 0.45;
  cursor: not-allowed;
}

/* ========== 费用与按钮 ========== */
.cost-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
  margin: 16px 0;
  padding: 12px 14px;
  background-color: #262626;
  border-radius: 10px;
  font-size: 14px;
}

.cost-row strong {
  color: #ffd54f;
  font-size: 18px;
  font-variant-numeric: tabular-nums;
}

.cost-row small {
  color: #9e9e9e;
  font-size: 12px;
}

.primary-btn,
.secondary-btn,
.danger-btn {
  min-height: 44px;
  padding: 10px 18px;
  border: 1px solid transparent;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease, opacity 0.2s ease;
}

.primary-btn {
  width: 100%;
  color: #fff;
  background-color: #4285f4;
}

.primary-btn:hover:not(:disabled) {
  background-color: #3367d6;
}

.primary-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.secondary-btn {
  color: #e8e8e8;
  background-color: transparent;
  border-color: #5a5a5a;
}

.secondary-btn:hover {
  background-color: rgba(255, 255, 255, 0.08);
}

.danger-btn {
  color: #fff;
  background-color: #c62828;
}

.danger-btn:hover {
  background-color: #ad1f1f;
}

.text-btn {
  min-height: 44px;
  padding: 0 8px;
  color: #8ab4f8;
  background: none;
  border: none;
  font-size: 13px;
  cursor: pointer;
}

.text-btn:hover {
  text-decoration: underline;
}

/* ========== 状态面板 ========== */
.status-panel {
  margin-top: 14px;
  padding: 12px 14px;
  background-color: #1a1a1a;
  border: 1px solid #333;
  border-radius: 10px;
  font-size: 13px;
  line-height: 1.6;
  color: #c8c8c8;
}

.status-panel p {
  margin: 0;
}

.status-panel__done {
  color: #a5d6a7;
}

.status-panel__failed {
  color: #ef9a9a;
}

.status-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
}

.status-actions .secondary-btn {
  min-height: 40px;
}

/* ========== 条款 ========== */
.terms {
  margin: 24px 0 0;
  color: #8f8f8f;
  font-size: 12px;
  line-height: 1.6;
  text-align: center;
}

/* ========== Toast ========== */
.toast-container {
  position: fixed;
  top: 80px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 12px;
  z-index: 10000;
  min-width: 280px;
  max-width: 90vw;
  padding: 14px 20px;
  border-radius: 10px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.35);
  font-size: 14px;
  font-weight: 500;
  animation: toast-in 0.25s ease-out;
}

.toast-container.success { background-color: #2e7d32; color: #fff; }
.toast-container.error   { background-color: #c62828; color: #fff; }
.toast-container.warning { background-color: #ef6c00; color: #fff; }
.toast-container.info    { background-color: #1565c0; color: #fff; }

.toast-icon {
  font-size: 18px;
  font-weight: 700;
}

@keyframes toast-in {
  from { opacity: 0; transform: translateX(-50%) translateY(-14px); }
  to   { opacity: 1; transform: translateX(-50%) translateY(0); }
}

/* ========== 弹窗 ========== */
.modal-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background-color: rgba(0, 0, 0, 0.65);
  z-index: 9999;
}

.modal {
  width: 100%;
  max-width: 420px;
  padding: 22px;
  background-color: #1f1f1f;
  border: 1px solid #333;
  border-radius: 14px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.4);
}

.modal--center {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
}

.modal h3 {
  margin: 0 0 10px;
  font-size: 17px;
  font-weight: 600;
}

.modal p {
  margin: 0 0 18px;
  color: #c8c8c8;
  font-size: 14px;
  line-height: 1.6;
}

.modal--center p {
  margin-bottom: 0;
}

.modal-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.modal-buttons .primary-btn {
  width: auto;
}

.spinner {
  width: 28px;
  height: 28px;
  border: 3px solid rgba(255, 255, 255, 0.25);
  border-top-color: #4285f4;
  border-radius: 50%;
  animation: spin 0.9s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ========== 响应式 ========== */
@media (max-width: 860px) {
  .stepwise-page {
    padding: 18px 14px 36px;
  }

  .step-card__body {
    grid-template-columns: minmax(0, 1fr);
  }

  .stepper {
    flex-direction: column;
    align-items: stretch;
  }

  .stepper-arrow {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .toast-container,
  .spinner {
    animation: none;
  }

  .upload-box,
  .toggle-slider,
  .toggle-slider::after,
  .primary-btn,
  .secondary-btn,
  .danger-btn {
    transition: none;
  }
}
</style>
